import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { PLAN_FORM_CSS as styles } from "../manage-plan-new/planFormTheme";
import {
  useSegments, useTiers, useAllPlans, useFeatureMaster, usePlanFeatures,
  useCreateTier, useUpdateTier,
} from "../hooks/useRateCard";

/**
 * Add or edit one pricing band.
 *
 * A tier is priced one of two ways and never both: a flat monthly fee, or a
 * per-trademark rate with a floor beneath it. Which one is on offer follows the
 * segment — corporate bands are flat, attorney-style bands are per mark — so the
 * form asks for one set of numbers, not both, and sends the other side as null.
 *
 * Features work the way they do on a plan: pick them from the same cards. Link a
 * plan and the tier inherits that plan's list instead, which is why the cards go
 * read-only rather than disappearing — an admin should see what will print.
 */

const EMPTY = {
  name: "",
  min_marks: "",
  max_marks: "",
  included_users: "",
  free_clients: "",
  price_monthly: "",
  per_mark_rate_yr: "",
  floor_monthly: "",
  onboarding_charge: "",
  extra_user_cost: "",
  extra_client_cost: "",
  quarterly_discount_pct: "",
  annual_discount_pct: "",
  max_additional_discount_pct: "",
  max_onboarding_discount_pct: "",
  gst_percent: "",
  overage_threshold_marks: "",
  overage_per_mark_yr: "",
  plan_id: "",
  sort_order: "",
  is_active: true,
};

/** Blank stays blank: an empty box means "not set", which is not the same as 0. */
const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toText = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * One numeric input.
 *
 * Declared out here, not inside the page: a component defined during render is a
 * new type every keystroke, and React remounts it — which is how a field loses
 * focus after every character typed.
 */
const Field = ({ name, label, placeholder, suffix, note, type = "number", required, form, set, errors }) => (
  <div className="asp-field">
    <label className="asp-label" htmlFor={name}>
      {label}{required && <span className="asp-req">*</span>}
    </label>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        id={name} name={name} type={type} className="form-control"
        value={form[name]} placeholder={placeholder}
        onChange={(e) => set({ [name]: e.target.value })}
        style={{ width: "100%" }}
      />
      {suffix && (
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#8b92a9", whiteSpace: "nowrap" }}>
          {suffix}
        </span>
      )}
    </div>
    {note && <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6, lineHeight: 1.5 }}>{note}</div>}
    {errors[name] && <div className="asp-error" style={{ marginTop: 8 }}>{errors[name]}</div>}
  </div>
);

const AddEditTier = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  /* The row travels with the click, so an edit opens filled in without a second
     round trip. Arriving cold — a refresh, a pasted URL — it is found in the
     segment's list instead. */
  const passed = location.state?.tier || null;
  const segment = params.get("segment") || passed?.segment || "corporate";

  const { data: segments = [] } = useSegments();
  const { data: tiers = [] } = useTiers(isEdit && !passed ? segment : null);
  const { data: plans = [] } = useAllPlans();
  const { data: masterFeatures = [], isLoading: featureLoading } = useFeatureMaster();

  const createTier = useCreateTier();
  const updateTier = useUpdateTier();
  const submitting = createTier.isPending || updateTier.isPending;

  const [form, setForm] = useState(EMPTY);
  const [features, setFeatures] = useState([]);
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState(!isEdit);

  const existing = passed || tiers.find((t) => String(t.id) === String(id));
  const currentSegment = segments.find((s) => s.slug === segment);
  /* Corporate is `is_attorney: 0` and priced flat; everything above it — attorney
     and any segment added later — is priced per trademark. */
  const perMark = Boolean(currentSegment && Number(currentSegment.is_attorney) !== 0);

  useEffect(() => {
    if (!isEdit || !existing || loaded) return;
    setForm({
      ...EMPTY,
      ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, toText(existing[k])])),
      is_active: existing.is_active !== false,
    });
    setLoaded(true);
  }, [isEdit, existing, loaded]);

  /* A linked plan's features, or — while editing — the ones this tier already
     resolves to, whichever way it got them. */
  const planId = toNum(form.plan_id);
  const { data: resolvedFeatures = [] } = usePlanFeatures({
    planId,
    rateCardId: planId ? null : (isEdit ? Number(id) : null),
  });

  /* Names, because that endpoint answers in names — matched back to ids so the
     same cards can show what is already selected. */
  const resolvedIds = useMemo(() => {
    const byName = new Map(masterFeatures.map((f) => [String(f.name || "").trim(), f.id]));
    return resolvedFeatures
      .map((feat) => byName.get(String(feat.name || "").trim()))
      .filter(Boolean);
  }, [resolvedFeatures, masterFeatures]);

  /* Only prefill from the resolved list once, and only while the admin has not
     started picking — otherwise every refetch would undo their choices. */
  const [featuresTouched, setFeaturesTouched] = useState(false);
  useEffect(() => {
    if (featuresTouched || !resolvedIds.length) return;
    setFeatures(resolvedIds);
  }, [resolvedIds, featuresTouched]);

  const inherited = Boolean(planId);
  const shown = inherited ? resolvedIds : features;

  const set = (patch) => { setForm((prev) => ({ ...prev, ...patch })); setErrors({}); };

  const toggleFeature = (featureId) => {
    if (inherited) return;
    setFeaturesTouched(true);
    setFeatures((prev) =>
      prev.includes(featureId) ? prev.filter((x) => x !== featureId) : [...prev, featureId],
    );
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "A tier needs a name — it is what the rep picks";
    if (perMark && form.per_mark_rate_yr === "") next.per_mark_rate_yr = "A per-trademark band needs a rate";
    if (!perMark && form.price_monthly === "") next.price_monthly = "A flat band needs a monthly price";
    if (!inherited && features.length === 0) next.features = "Pick what this tier includes, or link a plan";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const found = validate();
    if (Object.keys(found).length) { setErrors(found); return; }

    const body = {
      segment,
      name: form.name.trim(),
      min_marks: toNum(form.min_marks),
      max_marks: toNum(form.max_marks),
      included_users: toNum(form.included_users),
      free_clients: toNum(form.free_clients),
      // The unused shape is cleared, so a band can never carry two prices.
      price_monthly: perMark ? null : toNum(form.price_monthly),
      per_mark_rate_yr: perMark ? toNum(form.per_mark_rate_yr) : null,
      floor_monthly: perMark ? toNum(form.floor_monthly) : null,
      onboarding_charge: toNum(form.onboarding_charge),
      extra_user_cost: toNum(form.extra_user_cost),
      // Cleared alongside the field, so an old value cannot linger on a band
      // that has since gone unlimited.
      extra_client_cost: Number(form.free_clients) === -1 ? null : toNum(form.extra_client_cost),
      quarterly_discount_pct: toNum(form.quarterly_discount_pct),
      annual_discount_pct: toNum(form.annual_discount_pct),
      max_additional_discount_pct: toNum(form.max_additional_discount_pct),
      max_onboarding_discount_pct: toNum(form.max_onboarding_discount_pct),
      gst_percent: toNum(form.gst_percent),
      overage_threshold_marks: toNum(form.overage_threshold_marks),
      overage_per_mark_yr: toNum(form.overage_per_mark_yr),
      plan_id: planId,
      // Sent only when the tier carries its own; blank lets the plan's list stand.
      features: inherited ? null : features,
      sort_order: toNum(form.sort_order),
      is_active: form.is_active,
    };

    const done = { onSuccess: () => navigate("/cms/rate-card") };
    if (isEdit) updateTier.mutate({ id: Number(id), ...body }, done);
    else createTier.mutate(body, done);
  };

  const f = { form, set, errors };

  return (
    <>
      <style>{styles}</style>
      <div className="main-content asp-scope">
        <div className="page-content">
          <div className="container-fluid">

            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18">{isEdit ? "Edit Tier" : "Add Tier"}</h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">CMS</li>
                      <li className="breadcrumb-item"><Link to="/cms/rate-card">Rate Card</Link></li>
                      <li className="breadcrumb-item active">{isEdit ? "Edit Tier" : "Add Tier"}</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">

                      <div className="asp-card-header">
                        <div className="asp-card-icon">
                          <i className={`fas ${isEdit ? "fa-edit" : "fa-plus"}`} />
                        </div>
                        <div>
                          <div className="asp-card-title">{isEdit ? "Edit Tier" : "Add Tier"}</div>
                          <div className="asp-card-subtitle">
                            {currentSegment?.label || segment} ·{" "}
                            {perMark ? "priced per trademark" : "priced as a flat monthly fee"}
                          </div>
                        </div>
                        <span className={`asp-mode-badge ${isEdit ? "asp-mode-badge-edit" : "asp-mode-badge-create"}`}>
                          {isEdit ? "✏ Edit Mode" : "✦ Create Mode"}
                        </span>
                      </div>

                      <form onSubmit={handleSubmit}>
                        <div className="asp-form-body">

                          {/* ── Pricing ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Pricing</div>

                            <div className="asp-field">
                              <label className="asp-label" htmlFor="name">
                                Tier name<span className="asp-req">*</span>
                              </label>
                              <input
                                id="name" name="name" type="text" className="form-control"
                                value={form.name} placeholder="Business"
                                onChange={(e) => set({ name: e.target.value })}
                              />
                              <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6 }}>
                                This is what a rep sees in the Package dropdown.
                              </div>
                              {errors.name && <div className="asp-error" style={{ marginTop: 8 }}>{errors.name}</div>}
                            </div>

                            <div className="asp-price-grid">
                              {perMark ? (
                                <>
                                  <Field {...f}
                                    name="per_mark_rate_yr" label="Per-trademark rate" suffix="₹/mark/yr"
                                    placeholder="144" required
                                    note="A quote's actual rate comes from the segment's portfolio-size taper, by trademark count. This is the band's own reference rate."
                                  />
                                  <Field {...f}
                                    name="floor_monthly" label="Minimum / month" suffix="₹/mo" placeholder="2399"
                                    note="Only the entry band needs one — it is what a very small book still pays. Leave blank on the higher bands."
                                  />
                                </>
                              ) : (
                                <Field {...f} name="price_monthly" label="Monthly price (flat)" suffix="₹/mo" placeholder="11999" required />
                              )}
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f} name="min_marks" label="Min trademarks" placeholder="101" />
                              <Field {...f}
                                name="max_marks" label="Max trademarks" placeholder="1000"
                                note="Above the highest band the quote turns custom."
                              />
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f} name="included_users" label="Included users" placeholder="5" />
                              <Field {...f} name="extra_user_cost" label="Extra user cost" suffix="₹/mo" placeholder="1999" />
                            </div>

                            <div className="asp-price-grid">
                              <Field {...f}
                                name="free_clients" label="Free clients" placeholder="0"
                                note="−1 means unlimited. Blank if this band has no client book."
                              />
                              {/* An unlimited book has no extras to charge for, so
                                  the field would only collect a number nothing
                                  would ever apply. */}
                              {Number(form.free_clients) === -1 ? (
                                <div className="asp-field">
                                  <label className="asp-label">Extra client cost</label>
                                  <div style={{ fontSize: 12.5, color: "#8b92a9", padding: "10px 0" }}>
                                    Not charged — this band includes unlimited clients.
                                  </div>
                                </div>
                              ) : (
                                <Field {...f} name="extra_client_cost" label="Extra client cost" suffix="₹/mo" placeholder="0" />
                              )}
                            </div>

                            <Field {...f} name="sort_order" label="Sort order" placeholder="3" note="Where this band sits in the list." />
                          </div>

                          {/* ── Discounts ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Discounts</div>
                            <div className="asp-price-grid">
                              <Field {...f} name="quarterly_discount_pct" label="Quarterly prepay" suffix="%" placeholder="5" />
                              <Field {...f} name="annual_discount_pct" label="Annual prepay" suffix="%" placeholder="20" />
                            </div>
                            <div className="asp-price-grid">
                              <Field {...f}
                                name="max_additional_discount_pct" label="Max additional" suffix="%" placeholder="20"
                                note="The ceiling a rep can give away on their own."
                              />
                              <Field {...f}
                                name="max_onboarding_discount_pct" label="Max onboarding" suffix="%" placeholder="100"
                                note="100 lets onboarding be waived entirely."
                              />
                            </div>
                            <div className="asp-price-grid">
                              <Field {...f} name="onboarding_charge" label="Onboarding charge" suffix="₹" placeholder="9999" />
                              <Field {...f} name="gst_percent" label="GST" suffix="%" placeholder="18" />
                            </div>
                          </div>

                          {/* ── Overage ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Overage</div>
                            <div className="asp-price-grid">
                              <Field {...f} name="overage_threshold_marks" label="Threshold" suffix="marks" placeholder="—" />
                              <Field {...f} name="overage_per_mark_yr" label="Overage rate" suffix="₹/mark/yr" placeholder="—" />
                            </div>
                            <div style={{ fontSize: 12, color: "#8b92a9", lineHeight: 1.5 }}>
                              Only the top band of a segment usually charges for overage — leave
                              these blank everywhere else.
                            </div>
                          </div>

                          {/* ── Plan & features ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Plan &amp; Features</div>

                            <div className="asp-field">
                              <label className="asp-label" htmlFor="plan_id">Linked plan</label>
                              <select
                                id="plan_id" className="form-control" value={form.plan_id}
                                onChange={(e) => { set({ plan_id: e.target.value }); setFeaturesTouched(false); }}
                              >
                                <option value="">None — this tier carries its own features</option>
                                {plans.map((p) => (
                                  <option key={p.id} value={p.id}>{p.name || p.plan_name}</option>
                                ))}
                              </select>
                              <div style={{ fontSize: 12, color: "#8b92a9", marginTop: 6, lineHeight: 1.5 }}>
                                Linked, the tier prints that plan's features. Unlinked, pick them below.
                              </div>
                            </div>

                            {inherited && (
                              <div style={{ background: "#f4f6fd", border: "1px solid #dbe1f5", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#3b5bdb", lineHeight: 1.6 }}>
                                Inherited from the linked plan — clear the plan above to choose your own.
                              </div>
                            )}

                            {featureLoading ? (
                              <div className="asp-feature-grid">
                                {Array.from({ length: 6 }).map((_, i) => (
                                  <div key={i} style={{ background: "#f8f9fe", border: "1.5px solid #e2e6f0", borderRadius: 12, padding: 14 }}>
                                    <span className="asp-skeleton" style={{ width: "60%", height: 14, marginBottom: 8, display: "block" }} />
                                    <span className="asp-skeleton" style={{ width: "90%", height: 11 }} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="asp-feature-grid">
                                {[...masterFeatures]
                                  .sort((a, b) => (a.feature_order || 0) - (b.feature_order || 0))
                                  .map((item) => {
                                    const on = shown.includes(item.id);
                                    return (
                                      <div
                                        key={item.id}
                                        className={`asp-feature-card ${on ? "asp-feature-checked" : ""}`}
                                        style={inherited ? { cursor: "default", opacity: on ? 1 : 0.55 } : undefined}
                                      >
                                        <div className="asp-feature-card-top">
                                          <label className="asp-feature-check-label" htmlFor={`tier-feature-${item.id}`}>
                                            <input
                                              className="form-check-input" type="checkbox"
                                              id={`tier-feature-${item.id}`}
                                              checked={on} disabled={inherited}
                                              onChange={() => toggleFeature(item.id)}
                                            />
                                            <span className="asp-feature-name">{item.name}</span>
                                          </label>
                                        </div>
                                        <div className="asp-feature-desc">
                                          {item.description?.length > 60
                                            ? `${item.description.substring(0, 60)}…`
                                            : item.description}
                                        </div>
                                        <div className="asp-feature-selected-tick">✓</div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                            {errors.features && <div className="asp-error" style={{ marginTop: 12 }}>{errors.features}</div>}
                          </div>

                          {/* ── Active ── */}
                          <div className="asp-section">
                            <div className="asp-section-label">Availability</div>
                            <div className="asp-toggle-row">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input" type="checkbox" role="switch"
                                  id="is_active" checked={form.is_active}
                                  onChange={(e) => set({ is_active: e.target.checked })}
                                />
                              </div>
                              <div className="asp-toggle-info">
                                <strong className={form.is_active ? "ok" : "no"}>
                                  {form.is_active ? "Active" : "Inactive"}
                                </strong>
                                <br />
                                <small>
                                  {form.is_active
                                    ? "Reps can quote this band."
                                    : "Hidden from the Package dropdown. Quotes already sent keep their prices."}
                                </small>
                              </div>
                            </div>
                          </div>

                        </div>

                        <div className="asp-form-footer">
                          <Link to="/cms/rate-card" className="asp-btn asp-btn-cancel">Cancel</Link>
                          <button type="submit" className="asp-btn asp-btn-submit" disabled={submitting}>
                            {submitting
                              ? (isEdit ? "Updating…" : "Creating…")
                              : (isEdit ? "✓ Update Tier" : "+ Add Tier")}
                          </button>
                        </div>
                      </form>

                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default AddEditTier;
